import { omit } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

import { CreateFab } from "../../../components/CreateFab";
import { ErrorState } from "../../../components/States";
import {
  useGetLiabilitiesQuery,
  useGetLiabilitiesYearsQuery,
} from "../../../services";
import { useResponsive } from "../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../utils/constants";
import { LiabilitiesTotals } from "../types";
import { AddLiability } from "../panels/addLiability";
import {
  EmptyLiabilitiesState,
  LiabilitiesFilters,
  LiabilitiesHero,
  LiabilitiesHoldings,
  LiabilitiesInsights,
  LiabilitiesSkeleton,
} from "./components";
import { SortState } from "./components/LiabilitiesHoldings";
import { Wrapper } from "./styles";

const EMPTY_TOTALS: LiabilitiesTotals = {
  currentAmount: 0,
  amountLent: 0,
  paidAmount: 0,
  principalAmount: 0,
  interestAmount: 0,
};

// Sentinel period value covering every year, surfaced as the first option.
const ALL_TIME = "all";

export const LiabilitiesPage = () => {
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const [activeOnly, setActiveOnly] = useState(true);
  const [periodValue, setPeriodValue] = useState(ALL_TIME);
  const [sort, setSort] = useState<SortState>({ key: "debt", dir: "desc" });

  const selectedYear =
    periodValue === ALL_TIME ? undefined : Number(periodValue);

  const { data: yearsData } = useGetLiabilitiesYearsQuery();
  const years = useMemo(() => yearsData ?? [], [yearsData]);

  // The grand total ("X of Y liabilities") ignores the active-only filter, so
  // the unfiltered list tells us how many liabilities exist in total.
  const totalQuery = useGetLiabilitiesQuery({ activeOnly: false });
  const totalData = totalQuery.data;
  const totalLiabilities = totalData?.liabilities.length ?? 0;

  const listQuery = useGetLiabilitiesQuery({
    activeOnly,
    year: selectedYear,
  });
  const listData = listQuery.data;
  const liabilities = useMemo(() => listData?.liabilities ?? [], [listData]);
  const liabilitiesTotals: LiabilitiesTotals = listData
    ? omit(listData, "liabilities")
    : EMPTY_TOTALS;

  const periodOptions = useMemo(
    () => [
      { label: "All time", value: ALL_TIME },
      ...years.map((year) => ({
        label: year.toString(),
        value: year.toString(),
      })),
    ],
    [years],
  );

  // Latch the first load so filter refetches keep content mounted instead of flashing the skeleton.
  useEffect(() => {
    if (listData && totalData) {
      setHasLoaded(true);
    }
  }, [listData, totalData]);

  // Reset to All time when the selected year drops out of the available list
  // (e.g. after toggling Active only excludes its only liability).
  useEffect(() => {
    if (periodValue === ALL_TIME) {
      return;
    }
    if (!years.some((year) => year.toString() === periodValue)) {
      setPeriodValue(ALL_TIME);
    }
  }, [years, periodValue]);

  const navigateTo = (id: number) => {
    setLocation("/liabilities/" + id);
  };

  const handleClearFilters = useCallback(() => {
    setPeriodValue(ALL_TIME);
    setActiveOnly(false);
  }, []);

  const periodLabel = periodValue === ALL_TIME ? "All time" : periodValue;

  const isEmpty = liabilities.length === 0;
  const hasAnyLiabilities = totalLiabilities > 0;
  const isFilteredEmpty = isEmpty && hasAnyLiabilities;

  // Surface the FAB only where the Hero's "Add Liability" button is hidden: the
  // populated view on compact layouts. The empty state keeps its own button.
  const showCreateFab = isCompact && !isEmpty;
  const filtersVariant = isCompact ? "standalone" : "inline";

  const filterProps = {
    isCompact,
    periodValue,
    periodOptions,
    onPeriodChange: setPeriodValue,
    activeOnly,
    onActiveOnlyChange: setActiveOnly,
    count: liabilities.length,
    total: Math.max(totalLiabilities, liabilities.length),
    sort,
    onSortChange: setSort,
  };

  if ((listQuery.isError && !listData) || (totalQuery.isError && !totalData)) {
    return (
      <Wrapper>
        <ErrorState
          message="Unable to load liabilities. Please try again."
          onRetry={async () => {
            await Promise.all([listQuery.refetch(), totalQuery.refetch()]);
          }}
        />
      </Wrapper>
    );
  }

  if (!hasLoaded) {
    return <LiabilitiesSkeleton />;
  }

  const populatedView = (
    <>
      <LiabilitiesHero
        totals={liabilitiesTotals}
        liabilities={liabilities}
        periodLabel={periodLabel}
        onCreateLiability={() => setIsOpen(true)}
      />

      <LiabilitiesFilters
        variant={filtersVariant}
        {...filterProps}
        showSort={true}
      />

      <LiabilitiesInsights liabilities={liabilities} isCompact={isCompact} />

      <LiabilitiesHoldings
        liabilities={liabilities}
        onSelect={navigateTo}
        sort={sort}
        onSortChange={setSort}
      />
    </>
  );

  const emptyFilters = isFilteredEmpty ? (
    <LiabilitiesFilters
      variant={filtersVariant}
      {...filterProps}
      showSort={false}
    />
  ) : undefined;

  const emptyView = (
    <>
      <EmptyLiabilitiesState
        onCreateLiability={() => setIsOpen(true)}
        isFiltered={isFilteredEmpty}
        onClearFilters={handleClearFilters}
      />
      {emptyFilters}
    </>
  );

  return (
    <Wrapper>
      {isEmpty ? emptyView : populatedView}

      {showCreateFab && (
        <CreateFab onClick={() => setIsOpen(true)} ariaLabel="Add liability" />
      )}

      <AddLiability isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </Wrapper>
  );
};
